// Editorial Manager (Aries) submission preprocessor.
//
// OMJ manuscripts come from Editorial Manager as a system-generated PDF whose
// FIRST page is a structured cover/metadata sheet (not the manuscript):
//
//   Oman Medical Journal
//   <Title>
//   --Manuscript Draft--
//   Manuscript Number: …
//   Full Title: …
//   Article Type: Original Article | Review Article | …
//   Keywords: a; b; c
//   Corresponding Author: <name>  /  Corresponding Author's Institution: …
//   First Author: <name>
//   Order of Authors: <name>\n<name>\n…
//   Manuscript Region of Origin: …
//   Abstract: <structured abstract>
//   <then the actual manuscript body, often a clean copy AND a
//    line-numbered/marked copy concatenated together>
//
// Feeding that raw text straight into the pipeline corrupts everything:
//   • title detection returns "Oman Medical Journal"
//   • author/affiliation (COI) extraction misses the authoritative lists
//   • the duplicated body ~doubles word count, classifier scores, integrity
//     counts, and reference de-duplication
//   • the cover's "Abstract:" competes with the real "Abstract" heading
//
// This module extracts the authoritative cover metadata and returns a single
// clean manuscript body (cover stripped, duplicate copy collapsed) for the
// rest of the deterministic pipeline. Non–Editorial-Manager input (pasted
// text, other PDFs) passes through unchanged.

export interface EmCover {
  fullTitle: string | null;
  /** e.g. "Original Article", "Review Article", "Case Report". */
  articleType: string | null;
  /** Full author names in submission order (authoritative for COI). */
  authors: string[];
  /** Institution / region strings (authoritative for COI affiliation filter). */
  affiliations: string[];
  keywords: string[];
}

export interface EmPreprocessResult {
  isEditorialManager: boolean;
  cover: EmCover | null;
  /** Manuscript body: EM cover removed, duplicate copy collapsed. */
  cleanedText: string;
}

const FIELD_LABELS = [
  'Manuscript Number:',
  'Full Title:',
  'Short Title:',
  'Article Type:',
  'Section/Category:',
  'Keywords:',
  'Corresponding Author:',
  "Corresponding Author's Institution:",
  'Corresponding Author Secondary Information:',
  "Corresponding Author's Secondary Institution:",
  'First Author:',
  'First Author Secondary Information:',
  'Order of Authors:',
  'Order of Authors Secondary Information:',
  'Manuscript Region of Origin:',
  'Suggested Reviewers:',
  'Opposed Reviewers:',
  'Abstract:',
  'Additional Information:',
  'Response:',
];

function isEm(head: string): boolean {
  // Strongest single markers.
  if (/--\s*Manuscript Draft\s*--/i.test(head)) return true;
  if (/Powered by Editori?al Manager/i.test(head)) return true;
  if (/ProduXion Manager(?: from)? Aries Systems/i.test(head)) return true;
  // Older / varied EM templates: a Manuscript Number plus any one structured
  // cover field is a confident signal.
  const hasNumber = /\bManuscript Number:/i.test(head);
  const hasTitle = /\bFull Title:/i.test(head);
  const hasOrder = /\bOrder of Authors:/i.test(head);
  const hasType = /\bArticle Type:/i.test(head);
  if (hasTitle && hasOrder) return true;
  if (hasNumber && (hasTitle || hasOrder || hasType)) return true;
  return false;
}

// Editorial Manager renders apostrophes as a curly glyph ('); normalise so
// labels like "Corresponding Author's Institution:" match reliably.
function normApostrophes(s: string): string {
  return s.replace(/[‘’‛ʼ`]/g, "'");
}

const LABEL_TAIL_RE =
  /\s*(?:Corresponding Author|First Author|Order of Authors|Manuscript Region|Manuscript Number|Suggested Reviewers|Opposed Reviewers|Additional Information|Section\/Category|Short Title|Article Type|Keywords|Abstract|Response)\b[\s\S]*$/i;

/** Value of a single-line cover field (stops at the next known field label). */
function fieldValue(text: string, label: string): string | null {
  const norm = normApostrophes(text);
  const i = norm.indexOf(label);
  if (i < 0) return null;
  const after = norm.slice(i + label.length);
  // Capture until the next field label or a blank line.
  let end = after.length;
  for (const other of FIELD_LABELS) {
    const j = after.indexOf(other);
    if (j >= 0 && j < end) end = j;
  }
  // Belt-and-braces: cut any leaked label tail (handles label/spacing drift).
  const value = after.slice(0, end).replace(LABEL_TAIL_RE, '').replace(/\s+/g, ' ').trim();
  return value || null;
}

/** Multi-line list field (e.g. Order of Authors) → one entry per line. */
function listFieldValue(text: string, label: string): string[] {
  const norm = normApostrophes(text);
  const i = norm.indexOf(label);
  if (i < 0) return [];
  const after = norm.slice(i + label.length);
  let end = after.length;
  for (const other of FIELD_LABELS) {
    if (other === label) continue;
    const j = after.indexOf(other);
    if (j >= 0 && j < end) end = j;
  }
  return after
    .slice(0, end)
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length >= 2 && /[A-Za-z]/.test(l));
}

const HEADING_RE =
  /^(abstract|summary|introduction|background)\s*$/i;
const REF_HEADING_RE = /^(references?|bibliography|works cited)\s*$/i;

/** Offset of the first standalone body heading (real Abstract/Introduction). */
function firstBodyHeadingOffset(text: string): number {
  let cursor = 0;
  for (const line of text.split('\n')) {
    const t = line.trim();
    if (t.length > 0 && t.length <= 40 && HEADING_RE.test(t)) return cursor;
    cursor += line.length + 1;
  }
  return -1;
}

/**
 * If the body is duplicated (clean copy + line-numbered/marked copy that EM
 * concatenates), return the offset where the SECOND copy starts so we can
 * drop it. Heuristic: a standalone Abstract/Introduction heading that appears
 * AFTER a References heading.
 */
function secondCopyOffset(text: string, firstBodyStart: number): number {
  const lines = text.split('\n');
  let cursor = 0;
  let sawReferences = false;
  for (const line of lines) {
    const t = line.trim();
    if (cursor > firstBodyStart) {
      if (REF_HEADING_RE.test(t)) sawReferences = true;
      else if (sawReferences && t.length <= 40 && HEADING_RE.test(t)) return cursor;
    }
    cursor += line.length + 1;
  }
  return -1;
}

export function preprocessEditorialManager(rawText: string): EmPreprocessResult {
  const head = rawText.slice(0, 6000);
  if (!isEm(head)) {
    return { isEditorialManager: false, cover: null, cleanedText: rawText };
  }

  // Cover region = everything before the first real body heading.
  const bodyStart = firstBodyHeadingOffset(rawText);
  const coverRegion = bodyStart > 0 ? rawText.slice(0, bodyStart) : head;

  const fullTitle = fieldValue(coverRegion, 'Full Title:');
  const articleType = fieldValue(coverRegion, 'Article Type:');
  const keywordsRaw =
    fieldValue(coverRegion, 'Keywords:') ?? '';
  const keywords = keywordsRaw
    .split(/[;,]/)
    .map((k) => k.trim())
    .filter((k) => k.length > 1);

  const orderAuthors = listFieldValue(coverRegion, 'Order of Authors:');
  const firstAuthor = fieldValue(coverRegion, 'First Author:');
  const corrAuthor = fieldValue(coverRegion, 'Corresponding Author:');
  const authors = (
    orderAuthors.length > 0
      ? orderAuthors
      : [firstAuthor, corrAuthor].filter((a): a is string => Boolean(a))
  )
    // strip trailing degrees/credentials ("Kevin Cuandra, MD" → "Kevin Cuandra")
    .map((a) => a.replace(/,\s*(MD|PhD|MBBS|MSc|MPH|DDS|FRCP|FACP|RN|BSc|DVM)\b.*$/i, '').trim())
    .filter((a) => a.length >= 2)
    .slice(0, 40);

  const affiliations: string[] = [];
  const seen = new Set<string>();
  const addAffil = (v: string | null | undefined) => {
    if (!v) return;
    const cleaned = v.replace(LABEL_TAIL_RE, '').trim();
    const key = cleaned.toLowerCase();
    if (cleaned.length >= 4 && cleaned.length <= 200 && !seen.has(key)) {
      seen.add(key);
      affiliations.push(cleaned);
    }
  };
  for (const label of [
    "Corresponding Author's Institution:",
    "Corresponding Author's Secondary Institution:",
    'Manuscript Region of Origin:',
  ]) {
    addAffil(fieldValue(coverRegion, label));
  }
  // NOTE: we deliberately do NOT broadly harvest every institution-like line
  // from the cover. Real OMJ EM exports interleave a "Suggested Reviewers:"
  // block *before* the manuscript's own author-affiliation block, so a
  // heuristic harvest cannot reliably tell author institutions from
  // suggested-reviewer institutions — and ingesting the latter would wrongly
  // over-exclude valid reviewers in the COI filter. Full per-author
  // affiliation extraction needs a dedicated EM author/reviewer-block
  // delineator (tracked in docs/pipeline-audit.md). The authoritative
  // corresponding-author institution + region above, unioned with body
  // byline extraction in the extractor, is the safe baseline.

  // Clean body: from the first real heading; drop a duplicated second copy.
  let cleanedText = rawText;
  if (bodyStart >= 0) {
    const secondStart = secondCopyOffset(rawText, bodyStart);
    cleanedText = rawText
      .slice(bodyStart, secondStart > bodyStart ? secondStart : rawText.length)
      .trim();
    // Guard: if stripping left too little, fall back to the original text
    // (better a noisy analysis than an empty one).
    if (cleanedText.replace(/\s+/g, '').length < 500) cleanedText = rawText;
  }

  return {
    isEditorialManager: true,
    cover: { fullTitle, articleType, authors, affiliations, keywords },
    cleanedText,
  };
}

/** Last name token of a full name, lowercased — matches authors.ts output. */
export function surnameOf(fullName: string): string | null {
  const cleaned = fullName.replace(/[\d*†‡§¶]+$/g, '').trim();
  const parts = cleaned.split(/[\s.-]+/).filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i--) {
    const p = parts[i];
    if (p.length >= 2 && /^[A-Za-z][a-z'’-]+$/.test(p)) return p.toLowerCase();
  }
  return null;
}
